import { css } from "@mui/material/styles";
import { CSSProperties } from "react";
import { COLOR_BORDER } from "./colors";

export type FlexDirectionTypes =
  | "column"
  | "column-reverse"
  | "row"
  | "row-reverse";

export type JustifyContentTypes =
  | "stretch"
  | "space-evenly"
  | "space-around"
  | "space-between"
  | "end"
  | "start"
  | "center"
  | "right"
  | "left"
  | "flex-end"
  | "flex-start"
  | "inherit"
  | "initial"
  | "unset";

type FlexType = {
  ai?: string;
  jc?: string;
  fd?: FlexDirectionTypes;
};

export const flex = (props?: FlexType) => {
  const alignItems = props?.ai || "inherit";
  const justifyContent = props?.jc || "inherit";
  const flexDirection = props?.fd || "row";

  return css`
    display: -webkit-box;
    display: -moz-box;
    display: -ms-flexbox;
    display: -webkit-flex;
    display: flex;

    // Align Items
    ${alignItems === "flex-start" &&
    `-webkit-box-align: start;
      -moz-box-align: start;
      -ms-flex-align: start;
      `}

    ${alignItems === "flex-end" &&
    `-webkit-box-align: end;
      -moz-box-align: end;
      -ms-flex-align: end;
      `}

      ${alignItems !== "flex-start" &&
    alignItems !== "flex-end" &&
    `-webkit-box-align: ${alignItems};
      -moz-box-align: ${alignItems};
      -ms-flex-align: ${alignItems};
      `}

      -webkit-align-items: ${alignItems};
    align-items: ${alignItems};

    // Justify Content
    ${justifyContent === "flex-start" &&
    `-webkit-box-pack: start;
    -moz-box-pack: start;
    -ms-flex-pack: start;
      `}

    ${justifyContent === "flex-end" &&
    `-webkit-box-pack: end;
    -moz-box-pack: end;
    -ms-flex-pack: end;
      `}

      ${justifyContent === "space-between" &&
    `-webkit-box-pack: justify;
    -moz-box-pack: justify;
    -ms-flex-pack: justify;
      `}

      ${justifyContent === "space-around" && "-ms-flex-pack: distribute;"}

      ${justifyContent !== "flex-start" &&
    justifyContent !== "flex-end" &&
    justifyContent !== "space-between" &&
    justifyContent !== "space-around" &&
    `-webkit-box-pack: ${justifyContent};
    -moz-box-pack: ${justifyContent};
    -ms-flex-pack: ${justifyContent};
      `}

      -webkit-justify-content: ${justifyContent};
    justify-content: ${justifyContent};

    // Flex Direction
    flex-direction: ${flexDirection};
    -ms-flex-direction: ${flexDirection};
    -webkit-flex-direction: ${flexDirection};
  `;
};

type FlexCenterType = {
  fd?: FlexDirectionTypes;
};

export const flexCenter = (props?: FlexCenterType) =>
  flex({ ai: "center", jc: "center", fd: props?.fd });

type FlexColumnType = {
  ai?: string;
  jc?: string;
};

export const flexColumn = (props?: FlexColumnType) =>
  flex({ ai: props?.ai, jc: props?.jc, fd: "column" });

export const border = (width = 1, color = COLOR_BORDER) => css`
  border: ${width}px solid ${color};
`;

export const text = (textStyle: CSSProperties) => {
  const { fontFamily, fontSize, fontWeight, lineHeight, color } = textStyle;

  return css`
    font-family: ${fontFamily};
    font-size: ${fontSize};
    font-weight: ${fontWeight};
    line-height: ${lineHeight};
    color: ${color};
  `;
};

export const transition = (duration?: number) => css`
  transition: all ${duration || 0.25}s ease-in-out;
`;

export const fadeInAnimation = ({
  customInterval,
  start,
  end,
}: {
  customInterval?: number;
  start?: number;
  end?: number;
}) => {
  const interval = customInterval || 1;
  const opacity0 = start || 0;
  const opacity100 = end || 100;
  return css`
    animation: fadeIn ${interval}s;
    -webkit-animation: fadeIn ${interval}s;
    -moz-animation: fadeIn ${interval}s;
    -o-animation: fadeIn ${interval}s;
    -ms-animation: fadeIn ${interval}s;

    @keyframes fadeIn {
      0% {
        opacity: ${opacity0};
      }
      100% {
        opacity: ${opacity100};
      }
    }

    @-moz-keyframes fadeIn {
      0% {
        opacity: ${opacity0};
      }
      100% {
        opacity: ${opacity100};
      }
    }

    @-webkit-keyframes fadeIn {
      0% {
        opacity: ${opacity0};
      }
      100% {
        opacity: ${opacity100};
      }
    }

    @-o-keyframes fadeIn {
      0% {
        opacity: ${opacity0};
      }
      100% {
        opacity: ${opacity100};
      }
    }

    @-ms-keyframes fadeIn {
      0% {
        opacity: ${opacity0};
      }
      100% {
        opacity: ${opacity100};
      }
    }
  `;
};
